import { Controller } from '@hotwired/stimulus';

const ROW = '[data-profile-layout-target="row"]';
const LIST = '[data-profile-layout-target="list"]';
const DRAGGING = 'is-dragging';
const SORTING = 'is-sorting';

export default class extends Controller {
  static targets = ['list', 'row', 'handle', 'area', 'position', 'status', 'warning'];
  static values = { announcementTemplate: String };

  connect() {
    this.onMove = this.onMove.bind(this);
    this.onStop = this.onStop.bind(this);
    this.blockNativeDrag = (event) => event.preventDefault();
    this.handleTargets.forEach((handle) => handle.classList.remove('hidden'));
    this.renumber();
  }

  disconnect() {
    this.finish();
  }

  start(event) {
    if (event.button > 0) return;

    event.preventDefault();
    this.dragging = event.target.closest(ROW);
    if (!this.dragging) return;

    const bounds = this.dragging.getBoundingClientRect();
    this.originX = event.clientX;
    this.originY = event.clientY;
    this.originMiddle = bounds.top + bounds.height / 2;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    this.dragging.classList.add(DRAGGING);
    this.element.classList.add(SORTING);
    document.addEventListener('pointermove', this.onMove, { passive: false });
    document.addEventListener('pointerup', this.onStop);
    document.addEventListener('pointercancel', this.onStop);
    document.addEventListener('dragstart', this.blockNativeDrag);
  }

  onMove(event) {
    if (!this.dragging) return;

    event.preventDefault();
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    this.dragging.style.transform =
      `translate(${this.lastX - this.originX}px, ${this.lastY - this.originY}px)`;
    this.settle();
  }

  onStop() {
    if (this.dragging) this.announce(this.dragging);
    this.finish();
  }

  finish() {
    if (this.dragging) this.dragging.style.transform = '';
    this.dragging?.classList.remove(DRAGGING);
    this.element.classList.remove(SORTING);
    this.dragging = null;
    document.removeEventListener('pointermove', this.onMove);
    document.removeEventListener('pointerup', this.onStop);
    document.removeEventListener('pointercancel', this.onStop);
    document.removeEventListener('dragstart', this.blockNativeDrag);
  }

  settle() {
    const list = this.listAt(this.lastX, this.lastY);
    if (!list) return;

    const carriedMiddle = this.originMiddle + (this.lastY - this.originY);
    const neighbour = this.rowsIn(list).find((row) => {
      if (row === this.dragging) return false;
      const bounds = row.getBoundingClientRect();
      return carriedMiddle < bounds.top + bounds.height / 2;
    });
    const changed = neighbour
      ? neighbour.previousElementSibling !== this.dragging
      : list.lastElementChild !== this.dragging;
    if (!changed) return;

    if (neighbour) list.insertBefore(this.dragging, neighbour);
    else list.append(this.dragging);
    this.dragging.style.transform = '';
    const bounds = this.dragging.getBoundingClientRect();
    this.originX = this.lastX;
    this.originY = this.lastY;
    this.originMiddle = bounds.top + bounds.height / 2;
    this.renumber();
  }

  listAt(x, y) {
    return this.listTargets.find((list) => {
      const bounds = list.getBoundingClientRect();
      return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;
    });
  }

  moveWithKeyboard(event) {
    const offset = { ArrowUp: -1, ArrowDown: 1 }[event.key];
    if (!offset) return;

    event.preventDefault();
    this.moveRow(event.target.closest(ROW), offset);
  }

  moveUp(event) {
    event.preventDefault();
    this.moveRow(event.target.closest(ROW), -1, event.target);
  }

  moveDown(event) {
    event.preventDefault();
    this.moveRow(event.target.closest(ROW), 1, event.target);
  }

  moveArea(event) {
    event.preventDefault();
    const row = event.target.closest(ROW);
    const current = row.closest(LIST);
    const target = this.listTargets.find((list) => list !== current);
    target.append(row);
    this.renumber();
    event.target.textContent = this.moveLabel(target.dataset.area);
    event.target.focus();
    this.announce(row);
  }

  moveRow(row, offset, focus = row?.querySelector('[data-profile-layout-target="handle"]')) {
    if (!row) return;

    const list = row.closest(LIST);
    const rows = this.rowsIn(list);
    const index = rows.indexOf(row);
    const neighbour = rows[index + offset];
    if (!neighbour) return;

    neighbour.insertAdjacentElement(offset < 0 ? 'beforebegin' : 'afterend', row);
    this.renumber();
    focus?.focus();
    this.announce(row);
  }

  visibilityChanged() {
    this.warningTarget.classList.toggle('hidden', this.visibleCheckboxes.some((checkbox) => checkbox.checked));
  }

  renumber() {
    this.listTargets.forEach((list) => {
      this.rowsIn(list).forEach((row, index) => {
        row.querySelector('[data-profile-layout-target="area"]').value = list.dataset.area;
        row.querySelector('[data-profile-layout-target="position"]').value = index;
      });
    });
  }

  rowsIn(list) {
    return Array.from(list.querySelectorAll(`:scope > ${ROW}`));
  }

  get visibleCheckboxes() {
    return this.rowTargets.map((row) => row.querySelector('input[type="checkbox"]'));
  }

  announce(row) {
    if (!this.hasStatusTarget || !this.hasAnnouncementTemplateValue) return;

    const list = row.closest(LIST);
    const rows = this.rowsIn(list);
    const area = list.closest('section')?.querySelector('h2')?.textContent?.trim() || list.dataset.area;
    this.statusTarget.textContent = this.announcementTemplateValue
      .replace('%{section}', row.dataset.sectionLabel)
      .replace('%{area}', area)
      .replace('%{number}', rows.indexOf(row) + 1)
      .replace('%{count}', rows.length);
  }

  moveLabel(targetArea) {
    const key = targetArea === 'main' ? 'profileLayoutMoveSidebar' : 'profileLayoutMoveMain';
    return this.element.dataset[key];
  }
}
